import { AuthenticationHandler, AuthenticationResult, AuthenticationConfig } from './types';
import { appInsights } from '../../../utils/appInsights';

class ReactNativeAppAuthHandler implements AuthenticationHandler {
  private config: AuthenticationConfig;

  constructor(config: AuthenticationConfig) {
    this.config = config;
  }

  // eslint-disable-next-line class-methods-use-this
  async initialize(): Promise<AuthenticationResult> {
    try {
      appInsights.trackEvent({
        name: '[Auth] Initializing React Native App Auth',
        properties: { mode: 'react-native-app' }
      });

      const tokenFromStorage = ReactNativeAppAuthHandler.getTokenFromStorage();

      if (tokenFromStorage) {
        const isValidToken = await ReactNativeAppAuthHandler.validateToken(
          tokenFromStorage,
          this.config.domain,
          this.config.audience
        );

        if (isValidToken) {
          const nativeAuthClient = ReactNativeAppAuthHandler.createNativeAuthClient(this.config);

          return {
            isAuthenticated: true,
            auth0Client: nativeAuthClient as any
          };
        }
      }

      // No valid token available — wait for native app to inject one
      appInsights.trackTrace({
        message: '[ReactNativeAuth] Waiting for token injection from native app'
      });
      const injectedToken = await ReactNativeAppAuthHandler.waitForTokenInjection();

      if (injectedToken) {
        ReactNativeAppAuthHandler.storeToken(injectedToken);

        const nativeAuthClient = ReactNativeAppAuthHandler.createNativeAuthClient(this.config);
        return {
          isAuthenticated: true,
          auth0Client: nativeAuthClient as any
        };
      }

      appInsights.trackEvent({
        name: '[Auth] React Native authentication failed',
        properties: { reason: 'no-token-received' }
      });
      return { isAuthenticated: false };
    } catch (error) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error))
      });
      return { isAuthenticated: false };
    }
  }

  private static createNativeAuthClient(config: AuthenticationConfig) {
    return {
      getTokenSilently: async () => {
        const freshToken = ReactNativeAppAuthHandler.getTokenFromStorage();

        if (!freshToken) {
          throw new Error('No token available in storage');
        }

        const isValid = await ReactNativeAppAuthHandler.validateToken(
          freshToken,
          config.domain,
          config.audience
        );
        if (!isValid) {
          if ((window as any).ReactNativeWebView) {
            (window as any).ReactNativeWebView.postMessage(
              JSON.stringify({ type: 'REFRESH_TOKEN' })
            );

            const newToken = await ReactNativeAppAuthHandler.waitForTokenRefresh(5000);
            if (newToken) {
              return newToken;
            }

            throw new Error('Token expired and refresh failed');
          }

          throw new Error('Token expired - ReactNativeWebView not available');
        }

        return freshToken;
      },
      getUser: async () => {
        const currentToken = ReactNativeAppAuthHandler.getTokenFromStorage();
        if (!currentToken) return {};
        return ReactNativeAppAuthHandler.decodeJwtPayload(currentToken);
      },
      logout: async () => {
        ReactNativeAppAuthHandler.clearStoredTokens();
      }
    };
  }

  private static decodeJwtPayload(token: string): Record<string, any> {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      appInsights.trackException({
        exception: e instanceof Error ? e : new Error(String(e))
      });
      return {};
    }
  }

  private static getTokenFromStorage(): string | null {
    return localStorage.getItem('native_access_token');
  }

  private static async validateToken(
    token: string,
    expectedDomain?: string,
    expectedAudience?: string
  ): Promise<boolean> {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const clockSkewBuffer = 30;

      if (payload.exp <= currentTime + clockSkewBuffer) return false;

      if (expectedDomain && payload.iss !== `https://${expectedDomain}/`) return false;
      if (expectedAudience && payload.aud !== expectedAudience) return false;

      return true;
    } catch (error) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error))
      });
      return false;
    }
  }

  private static async waitForTokenInjection(timeout: number = 10000): Promise<string | null> {
    return new Promise((resolve) => {
      let timeoutId: NodeJS.Timeout;
      let resolved = false;
      const listeners: Array<{ event: string; handler: EventListener }> = [];

      const finish = (token: string | null) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutId);
        listeners.forEach(({ event, handler }) => window.removeEventListener(event, handler));
        resolve(token);
      };

      const handleNativeTokenReady = () => {
        const token = ReactNativeAppAuthHandler.getTokenFromStorage();
        if (token) finish(token);
      };

      const handleTokenInjection = (event: MessageEvent) => {
        if (event.data && event.data.type === 'AUTH_TOKEN_INJECTED') {
          finish(event.data.token);
        }
      };

      listeners.push(
        { event: 'nativeTokenReady', handler: handleNativeTokenReady as EventListener },
        { event: 'message', handler: handleTokenInjection as EventListener }
      );

      const checkForExistingToken = () => {
        if (resolved) return;

        const token = ReactNativeAppAuthHandler.getTokenFromStorage();
        if (token) {
          finish(token);
          return;
        }

        const injectedToken = (window as any).injectedAccessToken;
        if (injectedToken) {
          finish(injectedToken);
          return;
        }

        setTimeout(checkForExistingToken, 100);
      };

      window.addEventListener('nativeTokenReady', handleNativeTokenReady);
      window.addEventListener('message', handleTokenInjection);
      checkForExistingToken();

      timeoutId = setTimeout(() => {
        finish(null);
      }, timeout);
    });
  }

  private static async waitForTokenRefresh(timeout: number = 5000): Promise<string | null> {
    return new Promise((resolve) => {
      let timeoutId: NodeJS.Timeout;
      let resolved = false;
      const listeners: Array<{ event: string; handler: EventListener }> = [];

      const finish = (token: string | null) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutId);
        listeners.forEach(({ event, handler }) => window.removeEventListener(event, handler));
        resolve(token);
      };

      const handleTokenRefresh = () => {
        const token = ReactNativeAppAuthHandler.getTokenFromStorage();
        if (token) finish(token);
      };

      listeners.push({
        event: 'nativeTokenRefreshed',
        handler: handleTokenRefresh as EventListener
      });

      window.addEventListener('nativeTokenRefreshed', handleTokenRefresh);

      timeoutId = setTimeout(() => {
        finish(null);
      }, timeout);
    });
  }

  private static storeToken(token: string): void {
    localStorage.setItem('native_access_token', token);
  }

  private static clearStoredTokens(): void {
    localStorage.removeItem('native_access_token');
  }
}

export default ReactNativeAppAuthHandler;
